import React, { useState, useEffect } from "react";
import { ChevronRight, Loader2 } from "lucide-react";

const SeverityBadge = ({ severity }) => {
  const styles = {
    high: { bg: "bg-red-500/10", text: "text-red-400", dot: "bg-red-500", border: "border-red-500/20" },
    med: { bg: "bg-yellow-500/10", text: "text-yellow-400", dot: "bg-yellow-500", border: "border-yellow-500/20" },
    low: { bg: "bg-yellow-500/10", text: "text-yellow-400", dot: "bg-yellow-500", border: "border-yellow-500/20" },
  };

  const style = styles[severity] || styles.low;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot} shadow-[0_0_8px_currentColor]`} />
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
};

const AlertsPanel = ({ alerts, onAlertSelect, isLoading }) => {
  const [loadingIndex, setLoadingIndex] = useState(null);

  useEffect(() => {
    if (!isLoading) {
      setLoadingIndex(null);
    }
  }, [isLoading]);

  const handleAlertClick = (alert, index) => {
    if (isLoading) return; // Prevent multiple clicks
    setLoadingIndex(index);
    onAlertSelect?.(alert);
  };

  return (
    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 h-full animate-slide-up delay-200" style={{ animationFillMode: 'both' }}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="dark:text-white text-slate-900 font-bold text-lg">At-Risk Pipeline Alerts</h3>
      </div>

      <div className="space-y-3">
        {isLoading && alerts.length === 0 ? (
          [1, 2, 3].map(i => (
            <div key={i} className="flex flex-col sm:flex-row items-center gap-4 p-4 border dark:border-slate-800 border-slate-200 rounded-lg animate-pulse">
              <div className="h-6 w-16 dark:bg-slate-800 bg-slate-200 rounded-full"></div>
              <div className="flex-1 space-y-2 w-full">
                <div className="h-4 w-32 dark:bg-slate-800 bg-slate-200 rounded"></div>
                <div className="h-3 w-3/4 dark:bg-slate-800 bg-slate-200 rounded"></div>
              </div>
            </div>
          ))
        ) : alerts.length === 0 ? (
          <div className="text-center py-8 text-slate-500 dark:bg-slate-800/20 bg-slate-100 rounded-xl border border-dashed dark:border-slate-800 border-slate-300">
            <p>No active alerts. Good job!</p>
          </div>
        ) : (
          alerts.map((alert, index) => {
            const isItemLoading = loadingIndex === index;
            return (
              <button
                key={index}
                type="button"
                onClick={() => handleAlertClick(alert, index)}
                disabled={isLoading}
                className={`group w-full text-left flex items-start gap-4 p-4 dark:bg-slate-950/50 bg-slate-50 border dark:border-slate-800/80 border-slate-200 rounded-xl transition-all duration-300 ${isLoading && !isItemLoading ? "opacity-50 cursor-not-allowed" : "dark:hover:bg-slate-900 hover:bg-white dark:hover:border-slate-700 hover:border-slate-300 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
                  }`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <SeverityBadge severity={alert.severity} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold dark:text-slate-200 text-slate-800 truncate dark:group-hover:text-white group-hover:text-slate-900 transition-colors">
                      {alert.borrowerName || "Borrower"}
                    </p>
                    {alert.caseNumber && (
                      <span className="text-[10px] font-mono text-slate-500 dark:bg-slate-900 bg-white px-1.5 py-0.5 rounded border dark:border-slate-800 border-slate-200">
                        #{alert.caseNumber}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-1 line-clamp-2 leading-relaxed dark:group-hover:text-slate-300 group-hover:text-slate-500 transition-colors">{alert.text}</p>
                </div>
                <div className="self-center pl-2">
                  <div className="p-1.5 rounded-full dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 text-slate-600 group-hover:text-yellow-400 group-hover:bg-yellow-500/10 transition-colors">
                    {isItemLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AlertsPanel;
